import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import {
  ResourcesPageContainer,
  Section,
  SectionTitle,
  MediaGrid,
  MediaCard,
  MediaTitle,
  MediaMeta,
  MediaPlayerWrapper,
} from '../styles/ResourcesPageStyles';

const CACHE_KEY = 'resources_data';
const MAX_AGE_MS = 1000 * 60 * 60;

const ResourcesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const cached = await getCachedData(CACHE_KEY);

      if (cached && !isCacheStale(cached, MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoading(false);
      }

      try {
        const data = await get('/resources');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(CACHE_KEY, data);
        setLoading(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoading(false);
        }
        setError(err && err.message ? err.message : 'Failed to load resources page.');
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading && !pageData) {
    return (
      <ResourcesPageContainer>
        <LoadingSpinner />
      </ResourcesPageContainer>
    );
  }

  if (error && !pageData) {
    return <ResourcesPageContainer>{error}</ResourcesPageContainer>;
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const teachingVideos =
    pageData && Array.isArray(pageData.teachingVideos) ? pageData.teachingVideos : [];
  const otSessions = pageData && Array.isArray(pageData.otSessions) ? pageData.otSessions : [];
  const ntSessions = pageData && Array.isArray(pageData.ntSessions) ? pageData.ntSessions : [];
  const dbsSessions = pageData && Array.isArray(pageData.dbsSessions) ? pageData.dbsSessions : [];
  const nurturingSessions =
    pageData && Array.isArray(pageData.nurturingSessions) ? pageData.nurturingSessions : [];
  const audioLessons =
    pageData && Array.isArray(pageData.audioLessons) ? pageData.audioLessons : [];

  const renderMediaList = (items) => {
    if (!items || items.length === 0) return null;

    return (
      <MediaGrid>
        {items.map((item, index) => {
          const title = item.title || item.filename || 'Media file';
          const contentType = item.contentType || '';
          const uploadedAt = item.uploadedAt ? new Date(item.uploadedAt) : null;
          const url = item.url || null;
          const src = contentType && item.data
            ? `data:${contentType};base64,${item.data}`
            : null;

          const isAudio = contentType && contentType.startsWith('audio/');
          const isVideo = contentType && contentType.startsWith('video/');

          return (
            <MediaCard key={item._id || title || index}>
              <MediaTitle>{index + 1}. {title}</MediaTitle>
              <MediaMeta>
                {contentType && <span>{contentType}</span>}
                {contentType && uploadedAt && <span> • </span>}
                {uploadedAt && <span>{uploadedAt.toLocaleDateString()}</span>}
              </MediaMeta>

              {src && (
                <MediaPlayerWrapper>
                  {isAudio && (
                    <audio
                      controls
                      src={src}
                      style={{ width: '100%' }}
                    />
                  )}
                  {!isAudio && isVideo && (
                    <video
                      controls
                      src={src}
                      style={{ width: '100%' }}
                    />
                  )}
                  {!isAudio && !isVideo && (
                    <a href={src} download={item.filename || title}>
                      Download
                    </a>
                  )}
                </MediaPlayerWrapper>
              )}

              {!src && url && (
                <MediaPlayerWrapper>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open resource
                  </a>
                </MediaPlayerWrapper>
              )}
            </MediaCard>
          );
        })}
      </MediaGrid>
    );
  };

  return (
    <ResourcesPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {teachingVideos.length > 0 && (
        <Section>
          <SectionTitle>Teaching Videos</SectionTitle>
          {renderMediaList(teachingVideos)}
        </Section>
      )}

      {otSessions.length > 0 && (
        <Section>
          <SectionTitle>Old Testament Sessions</SectionTitle>
          {renderMediaList(otSessions)}
        </Section>
      )}

      {ntSessions.length > 0 && (
        <Section>
          <SectionTitle>New Testament Sessions</SectionTitle>
          {renderMediaList(ntSessions)}
        </Section>
      )}

      {dbsSessions.length > 0 && (
        <Section>
          <SectionTitle>DBS Sessions</SectionTitle>
          {renderMediaList(dbsSessions)}
        </Section>
      )}

      {nurturingSessions.length > 0 && (
        <Section>
          <SectionTitle>Nurturing Sessions</SectionTitle>
          {renderMediaList(nurturingSessions)}
        </Section>
      )}

      {audioLessons.length > 0 && (
        <Section>
          <SectionTitle>Audio Lessons</SectionTitle>
          {renderMediaList(audioLessons)}
        </Section>
      )}
    </ResourcesPageContainer>
  );
};

export default ResourcesPage;
